import { useEffect, useRef, useState } from 'react';
import type { GameType, RoomMode } from '@big2/shared';
import { Icon } from '../Icon.js';
import { CardUI } from '../CardUI.js';
import { DaaluuColorPicker } from '../game/daaluu/DaaluuColorPicker.js';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';
import { trackEvent } from '../../analytics.js';
import { getUserAvatar, getUserInitial } from '../../utils/avatar.js';
import { playMatchFoundSound } from '../../sound.js';
import './roomLauncher.css';

export interface RoomLauncherProps {
  nameInputRef: React.RefObject<HTMLInputElement>;
  name: string;
  setName: (name: string) => void;
  gameType: GameType;
  selectGameType: (gt: GameType) => void;
  mode: RoomMode;
  setMode: (mode: RoomMode) => void;
  customRoomName: string;
  setCustomRoomName: (name: string) => void;
  voiceChatEnabled: boolean;
  setVoiceChatEnabled: (enabled: boolean) => void;
  joinCode: string;
  setJoinCode: (code: string) => void;
  handleQuickPlay: () => void;
  handleCreate: (options?: { customName?: string; voiceChat?: boolean }) => void;
  handleJoin: (code?: string) => void;
  matchmakingStatus?: 'searching' | 'found' | 'timeout' | null;
  matchmakingElapsedSec?: number;
  matchmakingEstimatedSec?: number;
  onCancelMatchmaking?: () => void;
  onOpenCardBacks?: () => void;
  onOpenProfile?: () => void;
  onOpenRules?: (gameType: GameType) => void;
}

export function RoomLauncher({
  nameInputRef,
  name,
  setName,
  gameType,
  selectGameType,
  mode,
  setMode,
  customRoomName,
  setCustomRoomName,
  voiceChatEnabled,
  setVoiceChatEnabled,
  joinCode,
  setJoinCode,
  handleQuickPlay,
  handleCreate,
  handleJoin,
  matchmakingStatus = null,
  matchmakingElapsedSec = 0,
  matchmakingEstimatedSec = 20,
  onCancelMatchmaking,
  onOpenCardBacks,
  onOpenProfile,
  onOpenRules,
}: RoomLauncherProps) {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const isPaid = state.profile?.tier === 'paid';
  const [isEditingGuestName, setIsEditingGuestName] = useState(false);
  const [showHostModal, setShowHostModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [localJoinCode, setLocalJoinCode] = useState(joinCode);
  const [localCustomName, setLocalCustomName] = useState(customRoomName);
  const [localVoiceChat, setLocalVoiceChat] = useState(voiceChatEnabled);
  const [isJoining, setIsJoining] = useState(false);
  const [joinModalError, setJoinModalError] = useState<string | null>(null);
  const joinInputRef = useRef<HTMLInputElement>(null);

  const handleOpenJoinModal = () => {
    setJoinModalError(null);
    setIsJoining(false);
    actions.clearError();
    setShowJoinModal(true);
  };

  const handleCloseJoinModal = () => {
    setShowJoinModal(false);
    setJoinModalError(null);
    setIsJoining(false);
  };

  // If server emits an error while the join modal is active, catch it here and suppress global toast
  useEffect(() => {
    if (showJoinModal && state.error) {
      setJoinModalError(state.error);
      setIsJoining(false);
      actions.clearError();
    }
  }, [showJoinModal, state.error, actions]);

  // Focus and select input text on error so user can immediately retype
  useEffect(() => {
    if (joinModalError && joinInputRef.current) {
      joinInputRef.current.focus();
      joinInputRef.current.select();
    }
  }, [joinModalError]);

  // Allow closing modal via Escape key
  useEffect(() => {
    if (!showJoinModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isJoining) {
        handleCloseJoinModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showJoinModal, isJoining]);

  // Safety fallback if socket drops or join hangs
  useEffect(() => {
    if (!isJoining) return;
    const timer = setTimeout(() => {
      setIsJoining(false);
      setJoinModalError(isMn ? 'Холболт амжилтгүй боллоо. Дахин оролдоно уу.' : 'Connection timed out. Please try again.');
    }, 10_000);
    return () => clearTimeout(timer);
  }, [isJoining, isMn]);

  useEffect(() => {
    if (matchmakingStatus === 'found') {
      playMatchFoundSound();
    }
  }, [matchmakingStatus]);

  useEffect(() => {
    if (gameType === 'twentyone' && !state.supportedGames.includes('twentyone')) {
      selectGameType('big2');
    }
  }, [gameType, state.supportedGames, selectGameType]);

  // Tsai mode toggle is only shown if the user actually has Tsai or is VIP
  const hasTsaiTokens = Boolean(state.profile && (state.profile.tsaiBalance > 0 || isPaid) && gameType !== 'daaluu');

  const onConfirmCreateRoom = (e: React.FormEvent) => {
    e.preventDefault();
    setShowHostModal(false);
    handleCreate({ customName: localCustomName, voiceChat: localVoiceChat });
  };

  const onConfirmJoinRoom = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCode = localJoinCode.trim().toUpperCase();
    if (!cleanCode) return;
    setJoinModalError(null);
    setIsJoining(true);
    setJoinCode(cleanCode);
    handleJoin(cleanCode);
  };

  return (
    <div className="landing-hero-launcher" id="room-launcher">
      <div className="room-entry modern-launcher" data-testid="modern-room-launcher">
        {/* Rejoin Banner if previously disconnected from a game */}
        {state.lastRoomId && (
          <button
            type="button"
            className="room-rejoin"
            onClick={() => {
              if (name.trim()) localStorage.setItem('big2.name', name.trim());
              actions.rejoinRoom();
            }}
          >
            <Icon name="arrow-left-right" size={16} />
            {isMn ? 'Өмнөх өрөө рүү буцах' : 'Rejoin Match'} · {state.lastRoomId}
          </button>
        )}

        {/* 1. Identity Strip: No text input when logged in */}
        <div className="launcher-identity-strip">
          {state.profile ? (
            <div
              className="launcher-user-pill"
              onClick={onOpenProfile}
              role="button"
              tabIndex={0}
              title={isMn ? 'Профайл харах' : 'View profile'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onOpenProfile?.();
              }}
            >
              <span
                className="launcher-user-avatar"
                style={{
                  background: getUserAvatar(state.profile.id, state.profile.displayName).gradient,
                }}
                aria-hidden="true"
              >
                {getUserInitial(state.profile.displayName)}
              </span>
              <span className="launcher-user-name">{state.profile.displayName}</span>
              {isPaid && (
                <span className="room-vip-tag">
                  <Icon name="crown" size={10} /> VIP
                </span>
              )}
            </div>
          ) : (
            <div className="launcher-guest-chip">
              <span className="launcher-guest-lbl">{isMn ? 'Тоглогч:' : 'Playing as:'}</span>
              {isEditingGuestName ? (
                <input
                  ref={nameInputRef}
                  type="text"
                  value={name}
                  maxLength={16}
                  autoFocus
                  className="launcher-guest-input"
                  aria-label={t.yourName}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setIsEditingGuestName(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setIsEditingGuestName(false);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="launcher-guest-name-btn"
                  onClick={() => setIsEditingGuestName(true)}
                  title={isMn ? 'Нэр өөрчлөх' : 'Click to edit name'}
                >
                  <strong>{name || 'Guest'}</strong>
                  <span className="launcher-guest-edit-icon" aria-hidden="true">✏️</span>
                </button>
              )}
            </div>
          )}

          <div className="launcher-identity-right">
            {state.profile && state.profile.tsaiBalance > 0 && (
              <span className="launcher-tsai-balance" title={isMn ? 'Цайны үлдэгдэл' : 'Tsai balance'}>
                🍵 {state.profile.tsaiBalance}
              </span>
            )}
            {!state.profile && onOpenProfile && (
              <button type="button" className="launcher-signin-link" onClick={onOpenProfile}>
                {isMn ? 'Нэвтрэх' : 'Sign In'}
              </button>
            )}
          </div>
        </div>

        {/* 2. Responsive 2x2 Game Selection Grid */}
        <div className="launcher-game-tabs" role="tablist" aria-label={isMn ? 'Тоглоом сонгох' : 'Select Game'}>
          <button
            type="button"
            role="tab"
            data-game="big2"
            aria-selected={gameType === 'big2'}
            className={`launcher-game-tab${gameType === 'big2' ? ' is-active' : ''}`}
            onClick={() => selectGameType('big2')}
          >
            <span className="launcher-game-suit">♠</span>
            <span className="launcher-game-title">{isMn ? 'Арван гурав' : 'Big Two'}</span>
          </button>
          <button
            type="button"
            role="tab"
            data-game="muushig"
            aria-selected={gameType === 'muushig'}
            className={`launcher-game-tab${gameType === 'muushig' ? ' is-active' : ''}`}
            onClick={() => selectGameType('muushig')}
          >
            <span className="launcher-game-suit">🎲</span>
            <span className="launcher-game-title">{isMn ? 'Муушиг' : 'Muushig'}</span>
          </button>
          {state.supportedGames.includes('twentyone') && (
            <button
              type="button"
              role="tab"
              data-game="twentyone"
              aria-selected={gameType === 'twentyone'}
              className={`launcher-game-tab${gameType === 'twentyone' ? ' is-active' : ''}`}
              onClick={() => selectGameType('twentyone')}
            >
              <span className="launcher-game-suit">🃏</span>
              <span className="launcher-game-title">{isMn ? '21 (Очко)' : '21 (Ochko)'}</span>
            </button>
          )}
          {state.supportedGames.includes('daaluu') && (
            <button
              type="button"
              role="tab"
              data-game="daaluu"
              aria-selected={gameType === 'daaluu'}
              className={`launcher-game-tab${gameType === 'daaluu' ? ' is-active' : ''}`}
              onClick={() => selectGameType('daaluu')}
            >
              <span className="launcher-game-suit">
                <Icon name="domino" size={17} />
              </span>
              <span className="launcher-game-title">{isMn ? 'Даалуу' : 'Daaluu'}</span>
            </button>
          )}
        </div>

        {/* 3. Tsai Toggle (Conditional: Only shown if user has Tsai) */}
        {hasTsaiTokens && (
          <div className="launcher-mode-toggle" role="group" aria-label={t.gameModeLabel}>
            <button
              type="button"
              className={`launcher-mode-btn${mode === 'free' ? ' is-active' : ''}`}
              onClick={() => {
                setMode('free');
                trackEvent('room_mode_selected', { mode: 'free', source: 'welcome' });
              }}
            >
              {isMn ? '⚪ Чөлөөт' : '⚪ Free'}
            </button>
            <button
              type="button"
              className={`launcher-mode-btn${mode === 'tsai' ? ' is-active' : ''}`}
              onClick={() => {
                setMode('tsai');
                trackEvent('room_mode_selected', { mode: 'tsai', source: 'welcome' });
              }}
            >
              <span>{isMn ? '🍵 Цайтай' : '🍵 Tsai'}</span>
            </button>
          </div>
        )}

        {/* 4. Primary Hero Action: 1-Click Quick Play */}
        <div className="launcher-hero-action">
          {matchmakingStatus === 'searching' || matchmakingStatus === 'found' ? (
            <div className="launcher-queue-box" role="status" aria-live="polite">
              <span
                className={`matchmaking-radar-dot${matchmakingStatus === 'found' ? ' is-found' : ''}`}
                aria-hidden="true"
              />
              <div className="launcher-queue-text">
                <strong>
                  {matchmakingStatus === 'found'
                    ? isMn
                      ? '✨ Тоглолт олдлоо! Ширээ рүү орж байна...'
                      : '✨ Match Found! Starting game...'
                    : isMn
                    ? 'Тоглогч хайж байна...'
                    : 'Finding Match...'}
                </strong>
                <span>
                  ⏱️ {matchmakingElapsedSec}s / 20s · {isMn ? '20 сек-д тоглолт шууд эхэлнэ' : `Est ~${matchmakingEstimatedSec}s · Starts in 20s`}
                </span>
              </div>
              {matchmakingStatus === 'searching' && onCancelMatchmaking && (
                <button
                  type="button"
                  className="launcher-cancel-queue-btn"
                  onClick={onCancelMatchmaking}
                  title={isMn ? 'Хайлт цуцлах' : 'Cancel search'}
                >
                  ✕
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="launcher-quick-play-btn"
              onClick={handleQuickPlay}
              data-testid="launcher-quick-play-btn"
            >
              <span className="launcher-qp-main">⚡ {isMn ? 'Шуурхай тоглох' : 'Quick Play'}</span>
              <span className="launcher-qp-sub">
                {gameType === 'big2'
                  ? isMn
                    ? 'Шууд тоглолт эхлүүлнэ · 30 онооны өрөг'
                    : 'Instant game · 30-pt Big Two match'
                  : isMn
                  ? 'Шууд тоглолт эхлүүлнэ'
                  : 'Instant game · Starts immediately'}
              </span>
            </button>
          )}
        </div>

        {/* 5. Clean Divider & Secondary Actions for Friends */}
        <div className="launcher-friends-divider" aria-hidden="true">
          <span>{isMn ? 'эсвэл найзуудтайгаа тоглох' : 'or play with friends'}</span>
        </div>

        <div className="launcher-secondary-actions">
          <button
            type="button"
            className="launcher-secondary-btn"
            onClick={() => setShowHostModal(true)}
            disabled={matchmakingStatus === 'searching'}
          >
            <span>🏠 {isMn ? 'Хувийн өрөө үүсгэх' : 'Host Private Room'}</span>
          </button>

          <button
            type="button"
            className="launcher-secondary-btn"
            onClick={handleOpenJoinModal}
            disabled={matchmakingStatus === 'searching'}
          >
            <span>🔑 {isMn ? 'Кодоор орох' : 'Join by Code'}</span>
          </button>
        </div>

        {/* 6. Tools Row (Card Backs & Rules) */}
        <div className="room-tools">
          {gameType === 'daaluu' ? (
            <DaaluuColorPicker />
          ) : (
            onOpenCardBacks && (
              <button
                type="button"
                className="icon-button card-back-tool"
                onClick={onOpenCardBacks}
                aria-label={t.changeCardBack}
                title={t.changeCardBack}
                data-tooltip={t.changeCardBack}
              >
                <span>
                  <CardUI faceDown style={{ transform: 'scale(0.28)', transformOrigin: 'top left' }} />
                </span>
              </button>
            )
          )}
          {onOpenRules && (
            <button
              type="button"
              className="icon-button"
              onClick={() => onOpenRules(gameType)}
              aria-label={t.readRulesCta}
              title={t.readRulesCta}
              data-tooltip={t.readRulesCta}
            >
              <Icon name="list-ordered" />
            </button>
          )}
        </div>
      </div>

      {/* Host Private Room Modal */}
      {showHostModal && (
        <div className="launcher-dialog-backdrop" onClick={() => setShowHostModal(false)}>
          <div className="launcher-dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="launcher-dialog-header">
              <h3>{isMn ? '🏠 Найзуудын хувийн өрөө үүсгэх' : '🏠 Host Private Room'}</h3>
              <button
                type="button"
                className="launcher-dialog-close"
                onClick={() => setShowHostModal(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={onConfirmCreateRoom} className="launcher-dialog-body">
              <p className="launcher-dialog-desc">
                {isMn
                  ? 'Өрөө үүссэний дараа 5 оронтой урилгын код гарч ирэх бөгөөд найзууддаа илгээн хамтдаа тоглох боломжтой.'
                  : 'Creates an invite-only private table. Share the room code with your friends to play together.'}
              </p>

              {isPaid && (
                <div className="launcher-dialog-field">
                  <label htmlFor="custom-room-name-input">
                    {isMn ? 'Өрөөний тусгай нэр (VIP)' : 'Custom Room Name (VIP)'}
                  </label>
                  <input
                    id="custom-room-name-input"
                    type="text"
                    maxLength={20}
                    value={localCustomName}
                    placeholder={isMn ? 'Жишээ: POKER-NIGHT' : 'e.g. POKER-NIGHT'}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setLocalCustomName(val);
                      setCustomRoomName(val);
                    }}
                  />
                </div>
              )}

              <div className="launcher-dialog-field">
                <label
                  className="launcher-dialog-checkbox"
                  style={{ opacity: isPaid ? 1 : 0.65, cursor: isPaid ? 'pointer' : 'not-allowed' }}
                >
                  <input
                    type="checkbox"
                    checked={isPaid && localVoiceChat}
                    disabled={!isPaid}
                    onChange={(e) => {
                      setLocalVoiceChat(e.target.checked);
                      setVoiceChatEnabled(e.target.checked);
                    }}
                  />
                  <span>{isMn ? 'Өрөөнд дуут яриа идэвхжүүлэх' : 'Enable Voice Chat'}</span>
                  {!isPaid && <span className="room-vip-tag">VIP</span>}
                </label>
              </div>

              <div className="launcher-dialog-actions">
                <button type="submit" className="launcher-dialog-submit">
                  {isMn ? 'Өрөө үүсгэх' : 'Create Room'}
                </button>
                <button
                  type="button"
                  className="launcher-dialog-cancel"
                  onClick={() => setShowHostModal(false)}
                >
                  {isMn ? 'Цуцлах' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Join by Code Modal */}
      {showJoinModal && (
        <div
          className="launcher-dialog-backdrop"
          onClick={() => {
            if (!isJoining) handleCloseJoinModal();
          }}
        >
          <div className="launcher-dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="launcher-dialog-header">
              <h3>{isMn ? '🔑 Урилгын кодоор орох' : '🔑 Join by Invite Code'}</h3>
              <button
                type="button"
                className="launcher-dialog-close"
                onClick={handleCloseJoinModal}
                disabled={isJoining}
                aria-label={isMn ? 'Хаах' : 'Close'}
              >
                ✕
              </button>
            </div>
            <form onSubmit={onConfirmJoinRoom} className="launcher-dialog-body">
              {joinModalError && (
                <div className="launcher-dialog-error" role="alert">
                  <span className="launcher-dialog-error-icon" aria-hidden="true">⚠️</span>
                  <span>{t.translateServerError(joinModalError)}</span>
                </div>
              )}

              <div className="launcher-dialog-field">
                <label htmlFor="join-code-input">
                  {isMn ? 'Өрөөний 5 оронтой код эсвэл нэр' : 'Enter 5-character code or room name'}
                </label>
                <input
                  ref={joinInputRef}
                  id="join-code-input"
                  type="text"
                  maxLength={20}
                  autoFocus
                  disabled={isJoining}
                  className={joinModalError ? 'has-error' : undefined}
                  value={localJoinCode}
                  placeholder={isMn ? 'Жишээ: 8KJ2X' : 'e.g. 8KJ2X'}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase();
                    setLocalJoinCode(val);
                    setJoinCode(val);
                    if (joinModalError) setJoinModalError(null);
                  }}
                />
              </div>

              <div className="launcher-dialog-actions">
                <button
                  type="submit"
                  className="launcher-dialog-submit"
                  disabled={!localJoinCode.trim() || isJoining}
                >
                  {isJoining
                    ? isMn
                      ? 'Орж байна...'
                      : 'Joining...'
                    : isMn
                    ? 'Өрөө рүү орох'
                    : 'Join Table'}
                </button>
                <button
                  type="button"
                  className="launcher-dialog-cancel"
                  onClick={handleCloseJoinModal}
                  disabled={isJoining}
                >
                  {isMn ? 'Цуцлах' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
